import { useTranslation } from "react-i18next";
import { format } from "date-fns";
// import { IMaskInput } from "react-imask";
import type { FormikErrors } from "formik";
import { useEffect, useState } from "react";
import { CalendarDaysIcon } from "lucide-react";

import { cn } from "@/utils/cn";
import { ErrorMessage } from "../ErrorMessage";
import { IOSCalendar } from "@/components/IOSCalendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { AIShimmerOverlay } from "../ai/ai-info-extractor/ai-shimmer-overlay";

interface DatePickerInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size"
> {
  value?: Date;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  error?: FormikErrors<string>;
  containerStyle?: string;
  onChange: (date: Date | undefined) => void;
  size?: "sm" | "md";
  iconClassName?: string;
  showAIShimmer?: boolean;
  getExtraDayClass?: (day: Date) => string | undefined;
}

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  label,
  error,
  minDate,
  maxDate,
  containerStyle,
  disabled = false,
  size = "md",
  iconClassName,
  showAIShimmer = false,
  name,
  id,
  getExtraDayClass,
}) => {
  const { t, i18n } = useTranslation("common");
  const sizes = {
    sm: "h-10",
    md: "h-12",
  };
  const [open, setOpen] = useState(false);
  const [textValue, setTextValue] = useState(value ? format(value, "dd/MM/yyyy") : "");

  useEffect(() => {
    if (value instanceof Date && !isNaN(value.getTime()) && format(value, "dd/MM/yyyy") !== textValue) {
      setTextValue(format(value, "dd/MM/yyyy"));
    }
  }, [value]);

  // OLD PICKER, INPUT HANDLER
  // const handleTextChange = (val: string) => {
  //   setTextValue(val);
  //   if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
  //     const parsed = parse(val, "dd/MM/yyyy", new Date());
  //     if (parsed instanceof Date && !isNaN(parsed.getTime())) {
  //       onChange(parsed);
  //       return;
  //     }
  //   }
  // };

  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val) {
      const parsed = new Date(val + "T00:00:00");
      if (!isNaN(parsed.getTime())) {
        onChange(parsed);
      }
    } else {
      onChange(undefined);
    }
  };

  return (
    <div className={`flex flex-col gap-2 justify-start space-y-04 ${containerStyle}`}>
      {label && <label className="block text-xs text-border-strong font-medium uppercase">{label}</label>}
      <AIShimmerOverlay show={showAIShimmer}>
        <Popover open={open} onOpenChange={setOpen}>
          <div className="relative w-full flex flex-col">
            <div className={cn("relative w-full flex items-center", sizes[size])}>
              {/* OLD PICKER */}
              {/* <IMaskInput
              mask="00/00/0000"
              value={textValue}
              onAccept={(val: string) => handleTextChange(val)}
              disabled={disabled}
              placeholder="DD/MM/YYYY"
              className={cn(
                "flex w-full h-full relative items-center rounded-sxs border px-3 py-2 text-xs md:text-sm focus-visible:border-primary focus-visible:ring-primary focus-visible:outline-primary",
                error && "border-danger"
              )}
            /> */}
              <PopoverTrigger asChild disabled={disabled}>
                <Button
                  variant="outline"
                  className={cn(
                    "relative bg-transparent hover:bg-transparent text-muted-foreground gap-4 text-sm! w-full flex flex-row justify-between px-2 font-normal",
                    sizes[size],
                    {
                      "text-muted-foreground hover:text-muted-foreground": !value,
                      "text-foreground": value,
                    },
                  )}
                >
                  <CalendarDaysIcon className={`text-primary ${iconClassName}`} size={20} />
                  {value
                    ? value.toLocaleDateString(i18n.language)
                    : t("common:ui_kit.select_date")}
                </Button>
              </PopoverTrigger>
            </div>
            {error && (
              <ErrorMessage
                text={error.toString()}
                className="text-xs! mt-2"
                wrapperClassName="items-start! justify-start! p-0! h-auto!"
              />
            )}
          </div>
          <PopoverContent className="w-auto p-0 bg-white" align="center" sideOffset={10}>
            <div className="w-[280px] md:w-[300px] sm:max-w-[384px] overflow-hidden">
              <IOSCalendar
                value={value}
                selectionMode="single"
                onSelect={(d) => {
                  onChange(d as Date);
                  setOpen(false);
                }}
                label={label}
                minDate={minDate}
                maxDate={maxDate}
                onCancel={() => setOpen(false)}
                getExtraDayClass={getExtraDayClass}
              />
            </div>
          </PopoverContent>
        </Popover>
      </AIShimmerOverlay>
      <input
        type="date"
        name={name}
        id={id}
        autoComplete="bday"
        value={value ? format(value, "yyyy-MM-dd") : ""}
        onChange={handleHiddenInputChange}
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
      />
    </div>
  );
};
